import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, LogIn, LogOut, User, Search, Heart, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSelector from './LanguageSelector';
import CurrencySelector from './CurrencySelector';
import { AdBanner } from './AdBanner';
import { NotificationBell } from './header/NotificationBell';
import { CategoriesMegaMenu } from './CategoriesMegaMenu';
import { MobileCategoriesAccordion } from './MobileCategoriesAccordion';

const Header = ({ cartCount = 0 }) => {
  const { t } = useTranslation();
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleLogout = async () => {
    await logout();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  return (
    <>
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FFFF53]/95 border-b border-[rgba(28,36,30,0.12)] shadow-sm" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0" data-testid="logo-link">
            <img src="/logo.png" alt="VEEVAMMERCE" className="h-11 w-auto object-contain rounded-md" />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-lg font-bold tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif' }}><span className="text-[#D4A017]">{t('brand.name', 'VEEVAMMERCE')}</span> <span className="text-[#4A5568]">{t('brand.store', 'STORE')}</span></span>
              <span className="text-[10px] text-[#5C6B61] tracking-wider">{t('brand.tagline', 'Your First Choice for Best Products')}</span>
            </div>
          </Link>

          {/* Search Box - Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B61]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('nav.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 bg-white border border-[rgba(28,36,30,0.12)] rounded-full focus:outline-none focus:border-[#D96C4A] text-sm"
                data-testid="header-search-input"
              />
            </div>
          </form>

          <div className="flex items-center space-x-3">
            <CurrencySelector />
            <LanguageSelector />
            
            {isAuthenticated && (
              <Link to="/wishlist" className="relative" data-testid="wishlist-link">
                <Heart className="w-6 h-6 text-[#1C241E] hover:text-[#D96C4A] transition-colors" />
              </Link>
            )}

            {/* Notification Bell */}
            <NotificationBell isAuthenticated={isAuthenticated} />

            <Link to="/cart" className="relative" data-testid="cart-link">
              <ShoppingCart className="w-6 h-6 text-[#1C241E]" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#D96C4A] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>

            {isAuthenticated ? (
              <div className="hidden md:flex items-center space-x-4">
                <Link to="/profile" className="flex items-center space-x-2 text-[#1C241E] hover:text-[#D96C4A]" data-testid="user-menu">
                  <User className="w-5 h-5" />
                  <span className="text-sm">{user?.name}</span>
                </Link>
                <button 
                  onClick={handleLogout}
                  className="flex items-center space-x-2 text-[#1C241E] hover:text-[#D96C4A]"
                  data-testid="logout-button"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <Link to="/login" className="hidden md:flex items-center space-x-2 bg-[#D96C4A] hover:bg-[#C05A3D] text-white px-6 py-2 rounded-full" data-testid="login-link">
                <LogIn className="w-4 h-4" />
                <span>{t('nav.signIn')}</span>
              </Link>
            )}

            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-button">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Desktop Navigation — second row, gives the search box above room to breathe */}
        <nav className="hidden md:flex items-center space-x-6 mt-3 pt-3 border-t border-[rgba(28,36,30,0.15)]">
          <Link to="/" className="link-underline text-[#1C241E] hover:text-[#D96C4A] text-sm" data-testid="nav-home">{t('nav.home')}</Link>
          <Link to="/products" className="link-underline text-[#1C241E] hover:text-[#D96C4A] text-sm" data-testid="nav-products">{t('nav.products')}</Link>
          <CategoriesMegaMenu />
          <Link to="/about" className="link-underline text-[#1C241E] hover:text-[#D96C4A] text-sm" data-testid="nav-about">{t('nav.about', 'About Us')}</Link>
          <Link to="/contact" className="link-underline text-[#1C241E] hover:text-[#D96C4A] text-sm" data-testid="nav-contact">{t('nav.contact')}</Link>
          <Link to="/affiliate" className="link-underline text-[#D96C4A] hover:text-[#C05A3D] text-sm font-semibold" data-testid="nav-affiliate">{t('aff.becomeAffiliate', 'Become an Affiliate')}</Link>
          {isAuthenticated && (
            <>
              <Link to="/wishlist" className="link-underline text-[#1C241E] hover:text-[#D96C4A] text-sm" data-testid="nav-wishlist">{t('nav.wishlist')}</Link>
              <Link to="/orders" className="link-underline text-[#1C241E] hover:text-[#D96C4A] text-sm" data-testid="nav-orders">{t('nav.orders')}</Link>
              <Link to="/tickets" className="link-underline text-[#1C241E] hover:text-[#D96C4A] text-sm" data-testid="nav-tickets">{t('nav.support', 'Support')}</Link>
              {user?.role === 'admin' && (
                <Link to="/admin" className="link-underline text-[#D96C4A] hover:text-[#C05A3D] text-sm font-medium" data-testid="nav-admin">{t('nav.admin', 'Admin')}</Link>
              )}
            </>
          )}
        </nav>

        {/* Search Box - Mobile */}
        <form onSubmit={handleSearch} className="md:hidden mt-3">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B61]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('nav.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 bg-white border border-[rgba(28,36,30,0.12)] rounded-full focus:outline-none focus:border-[#D96C4A] text-sm"
              data-testid="header-search-input-mobile"
            />
          </div>
        </form>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 space-y-4" data-testid="mobile-menu">
            <Link to="/" className="block text-[#1C241E] hover:text-[#D96C4A]" onClick={() => setMobileMenuOpen(false)}>{t('nav.home')}</Link>
            <Link to="/products" className="block text-[#1C241E] hover:text-[#D96C4A]" onClick={() => setMobileMenuOpen(false)}>{t('nav.products')}</Link>
            <MobileCategoriesAccordion onNavigate={() => setMobileMenuOpen(false)} />
            <Link to="/about" className="block text-[#1C241E] hover:text-[#D96C4A]" onClick={() => setMobileMenuOpen(false)}>{t('nav.about', 'About Us')}</Link>
            <Link to="/contact" className="block text-[#1C241E] hover:text-[#D96C4A]" onClick={() => setMobileMenuOpen(false)}>{t('nav.contact')}</Link>
            <Link to="/affiliate" className="block text-[#D96C4A] hover:text-[#C05A3D] font-semibold" onClick={() => setMobileMenuOpen(false)}>{t('aff.becomeAffiliate', 'Become an Affiliate')}</Link>
            {isAuthenticated ? (
              <>
                <Link to="/wishlist" className="block text-[#1C241E] hover:text-[#D96C4A]" onClick={() => setMobileMenuOpen(false)}>{t('nav.wishlist')}</Link>
                <Link to="/orders" className="block text-[#1C241E] hover:text-[#D96C4A]" onClick={() => setMobileMenuOpen(false)}>{t('nav.orders')}</Link>
                <Link to="/tickets" className="block text-[#1C241E] hover:text-[#D96C4A]" onClick={() => setMobileMenuOpen(false)}>{t('nav.support', 'Support')}</Link>
                <Link to="/profile" className="block text-[#1C241E] hover:text-[#D96C4A]" onClick={() => setMobileMenuOpen(false)}>{t('nav.profile')}</Link>
                {user?.role === 'admin' && (
                  <Link to="/admin" className="block text-[#D96C4A] hover:text-[#C05A3D] font-medium" onClick={() => setMobileMenuOpen(false)}>{t('nav.admin', 'Admin')}</Link>
                )}
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block text-[#1C241E] hover:text-[#D96C4A] text-left w-full">
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="block text-[#D96C4A] hover:text-[#C05A3D] font-medium" onClick={() => setMobileMenuOpen(false)}>{t('nav.signIn')}</Link>
                <Link to="/register" className="block text-[#1C241E] hover:text-[#D96C4A]" onClick={() => setMobileMenuOpen(false)}>{t('auth.register', 'Register')}</Link>
              </>
            )}
          </nav>
        )}
      </div>
    </header>
    <AdBanner />
    </>
  );
};

export default Header;
